import React, { useEffect, FC } from 'react';
import {
    StyledListSelector,
    StyledUlListSelector,
    StyledEm,
    StyledSpan,
    StyledLinkRoute,
    StyleRouteIcon,
    StyleRoute,
    StyledInfo,
    StyledInfoSpan,
    StyledLinkViewMore,
    StyledMainSpan,
} from './styled';
import { Store } from '~/lib/data-contract/salesforce';
import { isNowBetweenDates, storeWillOpen } from '~/shared/utils/date-utils';
import { useTranslation } from '~/shared/utils/translation';
import { prettifyUrl } from '~/templates/pages/utils/prettifyUrl';
import { useFrame } from '~/shared/utils';

type Props = {
    selectOption?: (variant: any) => void;
    storeList: Store[] | null | undefined;
    selectedPin?: any;
};

export const StoreListUl: FC<Props> = (props) => {
    const { selectOption, storeList, selectedPin } = props;

    const { data: frame } = useFrame();

    const { translate } = useTranslation();
    const addIsOpenNow = (openingHours: Store['c_openingHours']) => {
        return (
            openingHours?.map((time) => ({
                ...time,
                isOpen: isNowBetweenDates(time.opens, time.closes),
                willOpenToday: storeWillOpen(time.opens),
            })) ?? []
        );
    };

    //Pin marker selected from list
    useEffect(() => {
        let el: HTMLElement | null;

        document
            .querySelectorAll('[class^="pinSelected"],[class*=" pinSelected"]')
            .forEach((el) => el.classList.remove('pinSelected'));
        if (selectedPin) {
            el = document.getElementById(selectedPin.dropPoint?._id);

            if (el) {
                el.className += ' pinSelected';
                el.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'start',
                });
            }
        }
    }, [selectedPin]);

    return (
        <StyledUlListSelector>
            {storeList?.map((storeOption, i) => {
                const openingHours = storeOption?.c_openingHours
                    ? addIsOpenNow(storeOption?.c_openingHours)
                    : [];
                const today = openingHours[0] ?? {};
                const willOpen = today.willOpenToday;
                const storeOpened = today?.isOpen;
                const openingHour = openingHours
                    .slice(1)
                    .find((schedule) => schedule.opens && schedule.closes);

                const storesUrl = frame?.settings?.pageReferences.storesPage?.url;

                const urlStore = storeOption?.name
                    ? `${storesUrl}/${prettifyUrl(storeOption?.name)}`
                    : '';
                const linkGoogle = `https://maps.google.com/?q=${storeOption.address1},${storeOption.postalCode} ${storeOption?.city}`;
                return (
                    <StyledListSelector
                        id={storeOption.id}
                        key={`${storeOption.id}-${i}-${storeOption.lng}-${storeOption.lat}`}
                        data-testid="store-list"
                    >
                        <div
                            tabIndex={0}
                            className="storeSummary"
                            onClick={() => selectOption?.(storeOption)}
                            onKeyDown={(e: React.KeyboardEvent) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    selectOption?.(storeOption);
                                    const routeLink = document.activeElement;

                                    if (
                                        routeLink?.tagName === 'A' &&
                                        routeLink?.getAttribute('href')
                                    ) {
                                        window
                                            .open(routeLink.getAttribute('href') ?? '', '_blank')
                                            ?.focus();
                                    }
                                }
                            }}
                        >
                            <StyledInfo>
                                <strong>{storeOption?.name ?? storeOption?.name}</strong>
                                <StyledMainSpan>
                                    {storeOption?.addressLine1 ?? storeOption?.address1}
                                </StyledMainSpan>
                                <StyledMainSpan>
                                    {storeOption?.addressLine2 ?? storeOption?.address2}
                                </StyledMainSpan>
                                <StyledMainSpan>
                                    {storeOption?.postalCode} {storeOption?.city}
                                </StyledMainSpan>
                                <StyledInfoSpan>
                                    <StyledEm storeOpened={storeOpened}>
                                        {translate(
                                            storeOpened ? 'pdp.openStore' : 'pdp.closedStore',
                                        )}
                                    </StyledEm>
                                    <span>.</span>
                                    <StyledSpan>
                                        {storeOpened
                                            ? translate('pdp.closesAt', {
                                                  closesAt: String(today.closes),
                                              })
                                            : translate('pdp.opensAt', {
                                                  opensAt: String(
                                                      willOpen ? today.day : openingHour?.day,
                                                  )?.toLocaleLowerCase(),
                                                  opensHour: String(
                                                      willOpen ? today.opens : openingHour?.opens,
                                                  ),
                                              })}
                                    </StyledSpan>
                                </StyledInfoSpan>
                            </StyledInfo>
                            <StyleRoute>
                                <StyledLinkRoute
                                    href={linkGoogle}
                                    title={translate('pdp.openStore')}
                                    target="_blank"
                                >
                                    <StyleRouteIcon />
                                    {translate('findStore.route')}
                                </StyledLinkRoute>
                            </StyleRoute>
                        </div>

                        <StyledLinkViewMore href={urlStore} data-testid="store-list-view">
                            {translate('findStore.view')}
                        </StyledLinkViewMore>
                    </StyledListSelector>
                );
            })}
        </StyledUlListSelector>
    );
};
